<template>
    <div class="gpa-addr-wrap">
        <input v-if="!selectedAddress && !showManualEntry" ref="autocomplete" v-model="address" type="text" placeholder="Enter your address" :disabled="!isGoogleLoaded" class="gpa-addr-input" @blur="handleBlur" :style="inputStyle"/>
        <div v-if="selectedAddress || showManualEntry">
            <div class="gpa-addr-row">
                <div class="gpa-addr-street"><input class="gpa-addr-input" v-model="manualEntry.street" maxlength="256" placeholder="Street Address" type="text" @blur="onAddressChange" :style="inputStyle" required></div>
                <div class="gpa-addr-apt"><input class="gpa-addr-input" v-model="manualEntry.address_line2" maxlength="256" placeholder="Apt #" type="text" @blur="onAddressChange" :style="inputStyle"></div>
            </div>

            <div class="gpa-addr-row gpa-addr-csz">
                <input v-model="manualEntry.city" class="gpa-addr-input gpa-addr-city" maxlength="256" placeholder="City" type="text" required @input="onAddressChange" :style="inputStyle">
                <div class="gpa-addr-state-wrap">
                    <input v-model="manualEntry.state" class="gpa-addr-input gpa-addr-state" maxlength="2" placeholder="State" type="text" required @input="handleStateInput" @focus="stateDropOpen = true" @blur="closeStateDrop" :style="inputStyle" autocomplete="address-level1">
                    <div v-if="stateDropOpen && filteredStates.length > 0" class="gpa-state-drop">
                        <div v-for="s in filteredStates" :key="s" class="gpa-state-opt" @mousedown.prevent="selectState(s)">{{ s }}</div>
                    </div>
                </div>
                <input v-model="manualEntry.zip" class="gpa-addr-input gpa-addr-zip" maxlength="10" placeholder="ZIP" type="text" required @input="onAddressChange" :style="inputStyle">
            </div>

            <div v-if="restrictedStateError" class="gpa-addr-restricted">{{ restrictedStateError }}</div>
        </div>
    </div>
</template>

<script>
import { isRestrictedState, RESTRICTED_STATE_MESSAGE } from '@/utils/restrictedStates';

export default {
    name: 'GooglePlacesAutocompleteNew',
    props: {
        apiKey: {
            type: String,
            required: true
        },
        borderColor: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            address: '',
            autocomplete: null,
            selectedAddress: null,
            manualEntry: {
                street: '',
                city: '',
                state: '',
                zip: '',
                address_line2: ''
            },
            isGoogleLoaded: false,
            showManualEntry: false,
            stateDropOpen: false,
            restrictedStateError: '',
            // LA and MS are deliberately absent — state law prohibits selling
            // GLP-1 medication there. Omitting them from this list is not the
            // gate on its own (the field is free text), so isRestrictedState()
            // is also enforced before any address leaves this component.
            // See src/utils/restrictedStates.js. Never add them back.
            usStates: [
                'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
                'HI','ID','IL','IN','IA','KS','KY','ME','MD',
                'MA','MI','MN','MO','MT','NE','NV','NH','NJ',
                'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
                'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
                'DC','PR','VI','GU','AS','MP'
            ]
        }
    },
    computed: {
        filteredStates() {
            const q = (this.manualEntry.state || '').toUpperCase();
            if (!q) return this.usStates;
            return this.usStates.filter(s => s.startsWith(q));
        },
        inputStyle() {
            if (!this.borderColor) {
                return {}
            }
            return {
                borderColor: this.borderColor,
                borderWidth: '1px',
                borderStyle: 'solid'
            }
        }
    },
    watch: {
        selectedAddress: {
            handler(newVal) {
                if (newVal) {
                    this.manualEntry = {
                        street: newVal.street,
                        city: newVal.city,
                        state: newVal.state,
                        zip: newVal.zip,
                        address_line2: newVal.address_line2 || ''
                    };
                }
            },
            immediate: true
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.loadGoogleMapsAPI()
        })
    },
    methods: {
        loadGoogleMapsAPI() {
            if (window.google && window.google.maps) {
                this.isGoogleLoaded = true
                this.initAutocomplete()
                return
            }

            const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api"]')
            if (existingScript) {
                const waitForGoogle = () => {
                    if (window.google && window.google.maps) {
                        this.isGoogleLoaded = true
                        this.initAutocomplete()
                    } else {
                        setTimeout(waitForGoogle, 100)
                    }
                }
                waitForGoogle()
                return
            }

            const script = document.createElement('script')
            script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places`
            script.async = true
            script.defer = true
            script.onload = () => {
                this.isGoogleLoaded = true
                this.initAutocomplete()
            }
            document.head.appendChild(script)
        },
        initAutocomplete() {
            if (!window.google || !this.$refs.autocomplete) {
                return
            }
            const options = {
                types: ['address'],
                componentRestrictions: {
                    country: 'us'
                }
            }
            this.autocomplete = new window.google.maps.places.Autocomplete(
                this.$refs.autocomplete,
                options
            )
            this.autocomplete.addListener('place_changed', this.handlePlaceSelect)
        },
        handlePlaceSelect() {
            const place = this.autocomplete.getPlace()
            if (!place.geometry) {
                console.log("No details available for input: '" + place.name + "'")
                return
            }

            this.selectedAddress = {
                street: '',
                city: '',
                state: '',
                zip: '',
                address_line2: ''
            }

            for (const component of place.address_components) {
                const type = component.types[0]
                if (type === 'street_number') {
                    this.selectedAddress.street = component.long_name + ' '
                }
                if (type === 'route') {
                    this.selectedAddress.street += component.long_name
                }
                if (type === 'locality') {
                    this.selectedAddress.city = component.long_name
                }
                if (type === 'administrative_area_level_1') {
                    this.selectedAddress.state = component.short_name
                }
                if (type === 'postal_code') {
                    this.selectedAddress.zip = component.long_name
                }
            }

            const formattedAddress = place.formatted_address
            const parts = formattedAddress.split(',')
            if (parts.length > 1) {
                const firstPart = parts[0].trim()
                if (firstPart.includes(' ')) {
                    const potentialSubpremise = firstPart.split(' ').pop()
                    if (potentialSubpremise.match(/^\d+[a-zA-Z]?$|^[a-zA-Z]$/)) {
                        this.selectedAddress.address_line2 = potentialSubpremise
                        this.selectedAddress.street = this.selectedAddress.street.replace(potentialSubpremise, '').trim()
                    }
                }
            }

            // A Google result can still land on a state we cannot ship to.
            if (this.blockRestrictedState(this.selectedAddress.state)) {
                // Drop the customer into manual entry so they can correct the
                // state instead of being stuck on a dead address.
                this.manualEntry = { ...this.selectedAddress };
                this.showManualEntry = true;
                return;
            }

            this.$emit('address-updated', this.selectedAddress)
        },
        // Single gate for Louisiana / Mississippi. Returns true when the state
        // is restricted, in which case NOTHING may be emitted to the parent.
        // GLP-1 medication cannot be sold there — this is state law.
        blockRestrictedState(state) {
            if (isRestrictedState(state)) {
                this.restrictedStateError = RESTRICTED_STATE_MESSAGE;
                return true;
            }
            this.restrictedStateError = '';
            return false;
        },
        handleBlur() {
            setTimeout(() => {
                if (this.address && this.address.trim() !== '' && !this.selectedAddress) {
                    this.showManualEntry = true;

                    const parts = this.address.split(',');
                    if (parts.length > 0) {
                        this.manualEntry.street = parts[0].trim();
                    }

                    if (parts.length > 1) {
                        const cityPart = parts[1].trim();
                        this.manualEntry.city = cityPart;
                    }

                    if (parts.length > 2) {
                        const statePart = parts[2].trim();
                        const stateZipMatch = statePart.match(/([A-Z]{2})\s+(\d{5}(-\d{4})?)/);
                        if (stateZipMatch) {
                            this.manualEntry.state = stateZipMatch[1];
                            this.manualEntry.zip = stateZipMatch[2];
                        } else {
                            const stateOnly = statePart.match(/^([A-Z]{2})$/);
                            this.manualEntry.state = stateOnly ? stateOnly[1] : '';
                        }
                    }
                }
            }, 200);
        },
        handleStateInput() {
            this.manualEntry.state = this.manualEntry.state.toUpperCase().replace(/[^A-Z]/g, '');
            this.stateDropOpen = true;
            // Surface the restricted-state message the moment they finish
            // typing LA or MS, before they reach any other field.
            if (this.blockRestrictedState(this.manualEntry.state)) return;
            if (this.usStates.includes(this.manualEntry.state)) {
                this.onAddressChange();
            }
        },
        selectState(s) {
            this.manualEntry.state = s;
            this.stateDropOpen = false;
            this.onAddressChange();
        },
        closeStateDrop() {
            setTimeout(() => { this.stateDropOpen = false; }, 150);
        },
        onAddressChange() {
            if (this.blockRestrictedState(this.manualEntry.state)) return;
            this.$emit('address-updated', this.manualEntry);
        },
        resetAddress() {
            this.selectedAddress = null;
            this.address = '';
            this.showManualEntry = false;
            this.restrictedStateError = '';
            this.manualEntry = {
                street: '',
                city: '',
                state: '',
                zip: '',
                address_line2: ''
            };
            this.$nextTick(() => {
                this.$refs.autocomplete.focus();
            });
        }
    }
}
</script>

<style scoped>
.gpa-addr-wrap {
    width: 100%;
}

.gpa-addr-input {
    width: 100%;
    height: 48px;
    padding: 10px 14px;
    font-size: 15px;
    font-family: inherit;
    color: #1a1a1a;
    background: #fff;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
    box-sizing: border-box;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(0, 0, 0, 0.02);
}

@media screen and (max-width: 991px) {
    .gpa-addr-input {
        height: 52px !important;
        box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(0, 0, 0, 0.02) !important;
    }
}

.gpa-addr-input:focus {
    border-color: var(--logo-blue, #52b2cf);
    box-shadow: 0 0 0 3px rgba(82, 178, 207, 0.15);
}

.gpa-addr-input::placeholder {
    color: #9ca3af;
    font-weight: 400;
}

.gpa-addr-state-wrap {
    position: relative;
    flex: 1;
    min-width: 80px;
    max-width: 100px;
}

.gpa-addr-state-wrap .gpa-addr-state {
    text-transform: uppercase;
}

.gpa-state-drop {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    max-height: 180px;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #d1d5db;
    border-top: none;
    border-radius: 0 0 8px 8px;
    z-index: 100;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.gpa-state-opt {
    padding: 8px 14px;
    font-size: 14px;
    cursor: pointer;
    color: #1a1a1a;
}

.gpa-state-opt:hover {
    background: #f3f4f6;
}

.gpa-addr-row {
    display: flex;
    gap: 10px;
    margin-bottom: 10px;
}

.gpa-addr-street {
    flex: 2;
}

.gpa-addr-apt {
    flex: 1;
    max-width: 120px;
}

.gpa-addr-csz {
    display: flex;
    gap: 10px;
}

.gpa-addr-city {
    flex: 2;
}

.gpa-addr-zip {
    flex: 1;
    min-width: 80px;
    max-width: 110px;
}

.gpa-addr-restricted {
    margin-top: 8px;
    color: #dc2626;
    font-size: 13px;
    line-height: 1.4;
}

@media screen and (max-width: 479px) {
    .gpa-addr-input {
        height: 44px;
        font-size: 14px;
        padding: 8px 12px;
    }

    .gpa-addr-row {
        gap: 8px;
        margin-bottom: 8px;
    }

    .gpa-addr-apt {
        max-width: 90px;
    }

    .gpa-addr-state-wrap {
        max-width: 85px;
    }

    .gpa-addr-zip {
        max-width: 90px;
    }
}
</style>
